/** @format */

import React, { useState } from 'react'
import './companyPicker.css'
import { useDispatch, useSelector } from 'react-redux'
import { setCompany } from '../../redux/actions/companyAction'
import { Box, Modal } from '@mui/material'
import { useLocation } from 'react-router-dom'
import { t } from 'i18next'
import GenericModal from 'Components/GenericModal'
import { useCheckFinalPrice } from 'Components/ActionTable/hooks/useCheckFinalPrice'
import { searchCompanyName } from 'Utils/functions'

function CompanyPicker({
  show,
  setShow,
  home,
  companies,
  confirmRemovalIn,
  onPriceRefreshError,
}) {
  const channel = new BroadcastChannel('customer_display')
  const { applyCompanyChange } = useCheckFinalPrice()
  const dispatch = useDispatch()
  const { pathname } = useLocation()

  const activeCompany = useSelector(state => state?.company)

  const allWarehouses = useSelector(
    state => state?.login?.user?.CustomerInfo?.Warehouses
  )

  const customerId = useSelector(
    state => state.cotizacion?.QuoteHeader?.CustomerId
  )

  const [selectedCompany, setSelectedCompany] = useState(activeCompany)
  const [pendingCompany, setPendingCompany] = useState(null)
  const isLoading = useSelector(state => state.cart.isLoadingAllProducts)

  const getCompanyWarehouses = company =>
    (allWarehouses ?? []).filter(
      warehouse => warehouse.CompanyId === company.CompanyId
    )

  // A company without branches assigned to the user cannot become active.
  const isCompanyValid = company =>
    company.CompanyId !== selectedCompany.CompanyId &&
    company?.IsVisibleInPortalVAD &&
    getCompanyWarehouses(company).length > 0

  const handleChangeCompany = async company => {
    if (isLoading) return
    setPendingCompany(null)
    const warehouses = getCompanyWarehouses(company)
    if (!warehouses.length) {
      console.error('No branches assigned to company:', company?.CompanyId)
      return
    }
    const [warehouse] = warehouses
    channel.postMessage({
      isChangeCompany: true,
      company: warehouse.CompanyId,
    })
    let applied = false
    try {
      // The cart is priced first so the user can cancel before switching
      applied =
        (await applyCompanyChange({
          pathname,
          newCompany: warehouse,
          previousCompanyId: activeCompany.CompanyId,
          customerId,
          confirmRemoval: confirmRemovalIn?.(warehouse.WarehouseName),
        })) !== false
    } catch (err) {
      console.error('checkFinalPrice error on company change:', err)
      onPriceRefreshError?.()
    }

    if (applied) {
      window.localStorage.setItem('company', searchCompanyName(company))
      setSelectedCompany(company)
      dispatch(
        setCompany({
          CompanyCode: warehouse.CompanyCode,
          CompanyName: warehouse.CompanyName,
          CompanyId: warehouse.CompanyId,
          WarehouseCode: warehouse.WarehouseCode,
          WarehouseId: warehouse.WarehouseId,
          WarehouseDescription: warehouse.WarehouseDescription,
          WarehouseName: warehouse.WarehouseName,
          WarehouseAddress: warehouse.WarehouseAddress,
          DefaultWarehouse: warehouses.length === 1,
          ProfileImage: company?.ProfileImage,
        })
      )
    }

    const current = applied ? warehouse : activeCompany
    channel.postMessage({
      company: current.CompanyId,
      isChangeCompany: false,
    })
    setShow(false)
  }

  const handleSelectCompany = company => {
    if (isLoading) return
    if (
      pathname.includes('editar_cotizacion') ||
      pathname.includes('crear_cotizacion') ||
      pathname.includes('cotizar_producto')
    ) {
      setPendingCompany(company)
    } else {
      handleChangeCompany(company)
    }
  }

  return (
    <>
      <Box
        component="div"
        className={show ? 'companypicker-menu' : 'companypicker-menu-off'}
        sx={{
          left: {
            xs: '0',
            sm: home ? '3px' : '75px',
          },
        }}
        data-testid="container-company-picker">
        <ul data-testid="list-companies">
          {companies?.length &&
            companies
              .filter(company => isCompanyValid(company))
              .map(company => (
                <li
                  key={company.CompanyId}
                  className="company-element"
                  onClick={() => handleSelectCompany(company)}
                  data-testid={`item-company-${company.CompanyId}`}>
                  <img src={company?.ProfileImage} alt="novey-logo" />
                </li>
              ))}
        </ul>
      </Box>
      <Modal
        open={!!pendingCompany}
        onClose={() => setPendingCompany(null)}
        data-testid="modal-confirm-company-change">
        <Box>
          <GenericModal
            hideCheck={true}
            showCaution={true}
            setConfirm={() => handleChangeCompany(pendingCompany)}
            setIsOpen={() => setPendingCompany(null)}
            text={t('GENERIC_MODAL.ARE_SURE_TO_CHANGE')}
            mostrarButtons={true}
            text2={t('GENERIC_MODAL.WANT_TO_CONTINUE')}
          />
        </Box>
      </Modal>
    </>
  )
}

export default CompanyPicker
